import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dag4gdrep053ijub23pg';

const principles = [
  { icon: 'ri-dashboard-line', title: '分类索引', desc: '以分类、标签、专题、栏目、目录多维度组织内容，查找有章可循。' },
  { icon: 'ri-leaf-line', title: '脉络优先', desc: '把热点还原成完整时间线，注重梳理来龙去脉而非简单堆料。' },
  { icon: 'ri-shield-check-line', title: '理性围观', desc: '传递娱乐资讯，倡导理性讨论，不渲染、不偏听偏信。' },
  { icon: 'ri-refresh-line', title: '持续更新', desc: '高频栏目每日更新，专题与长内容按周稳定产出。' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const trap = String(fd.get('website_alt') || '').trim();
    if (trap) {
      setStatus('success');
      setLoading(false);
      return;
    }
    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== 'website_alt' && value != null && String(value).trim() !== '') {
        body.append(key, String(value).trim());
      }
    });
    setLoading(true);
    setStatus('idle');
    setFormError('');
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = (meta?.message || parsed?.message || meta?.detail) as string | undefined;
      const serverText = serverMsg || responseText;
      const isSpam = typeof serverText === 'string' && serverText.includes('spam');
      const okCode = parsed?.code === 'OK';
      if (!res.ok || !okCode || isSpam) {
        setFormError(typeof serverText === 'string' && serverText ? serverText : '提交失败，请稍后再试。');
        setStatus('error');
      } else {
        setStatus('success');
        form.reset();
      }
    } catch {
      setFormError('网络异常，请检查后重试。');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full rounded-xl border border-background-200 bg-background-100/70 px-4 py-2.5 text-sm text-foreground-800 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-300/50';

  return (
    <section id="contact" className="scroll-mt-20 border-t border-background-200/70 bg-background-100/60 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary-700">
              <span className="inline-block h-2 w-2 rounded-full bg-primary-500" aria-hidden="true"></span>
              About Us
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于导航吃瓜</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-600">
              导航吃瓜是一个面向吃瓜爱好者的话题内容索引门户。我们相信，真正有价值的不是堆砌信息，而是让信息<span className="font-semibold text-foreground-900">找得到、读得懂、追得上</span>。于是我们把散落在影视、综艺、明星、网红与直播等方向的热点内容，统一收纳进一套清晰可循的分类体系。
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-foreground-600">
              从“今日热榜”到“分类导航”，从“常设栏目”到“精品专题”，每一层都服务于同一个目标——让你不再大海捞针，轻松锁定自己感兴趣的吃瓜方向。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {principles.map((p) => (
                <div key={p.title} className="rounded-2xl border border-background-200/80 bg-background-50 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                    <i className={p.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-2.5 text-sm font-bold text-foreground-900">{p.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-500">{p.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-background-200/80 bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {['瓜友小陈', '追剧少女', '吃瓜老编', '闪姐'].map((n, i) => (
                    <span
                      key={n}
                      className={`flex h-9 w-9 items-center justify-center rounded-full border-2 border-background-50 text-xs font-semibold text-background-50 ${
                        ['bg-primary-500', 'bg-accent-500', 'bg-secondary-500', 'bg-foreground-700'][i]
                      }`}
                      title={n}
                    >
                      {n.charAt(0)}
                    </span>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-secondary-600" aria-label="评分 4.9">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-half-line"></i>
                    <span className="ml-1 text-sm font-semibold text-foreground-900">4.9</span>
                  </div>
                  <p className="mt-0.5 text-xs text-foreground-500">来自 3.2 万位瓜友的使用反馈 · 收录效率备受好评</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:pt-8">
            <div className="rounded-3xl border border-background-200/80 bg-background-50 p-6 md:p-7">
              <h3 className="font-heading text-xl font-bold text-foreground-950">收录建议与意见反馈</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                有想看的分类方向、专题内容或栏目建议？欢迎告诉我们，我们会评估后纳入后续整理计划。
              </p>

              {status === 'success' ? (
                <div className="mt-6 flex flex-col items-center rounded-2xl bg-accent-50 px-5 py-10 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-2xl text-background-50">
                    <i className="ri-check-line" aria-hidden="true"></i>
                  </span>
                  <p className="mt-4 font-semibold text-foreground-900">已收到你的建议</p>
                  <p className="mt-1 text-sm text-foreground-500">感谢反馈，我们会认真评估每一条收录方向。</p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 rounded-full bg-background-200/80 px-5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200"
                  >
                    再提一条
                  </button>
                </div>
              ) : (
                <form id="navi-feedback-form" data-readdy-form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <input
                    type="text"
                    name="website_alt"
                    className="nav-entry-trap"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-700">怎么称呼你</label>
                      <input id="fb-name" name="name" type="text" placeholder="例如：小瓜" className={inputClass} maxLength={30} />
                    </div>
                    <div>
                      <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-700">联系邮箱</label>
                      <input id="fb-email" name="email" type="email" required placeholder="you@example.com" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="fb-topic" className="mb-1.5 block text-sm font-medium text-foreground-700">建议类型</label>
                    <select id="fb-topic" name="topic" className={inputClass} defaultValue="分类建议">
                      <option>分类建议</option>
                      <option>专题收录</option>
                      <option>栏目收录</option>
                      <option>页面反馈</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="fb-msg" className="mb-1.5 block text-sm font-medium text-foreground-700">具体建议 <span className="text-foreground-400">（500 字以内）</span></label>
                    <textarea
                      id="fb-msg"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="你希望看到什么分类、专题或栏目？"
                      className={`${inputClass} resize-none`}
                    ></textarea>
                  </div>

                  {status === 'error' && formError ? (
                    <div className="flex items-start gap-2 rounded-xl bg-primary-100/70 px-4 py-3 text-sm text-primary-900">
                      <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                      <span>{formError}</span>
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    ) : (
                      <i className="ri-send-plane-fill" aria-hidden="true"></i>
                    )}
                    {loading ? '提交中…' : '提交建议'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}